import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceAtRules() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">⚡ CSS At-Rules</h1>

      {/* Intro */}
      <p className="tutorial-intro" style={{ marginBottom: "30px" }}>
        CSS <strong>At-Rules</strong> are instructions that provide conditional
        or global rules for CSS. They start with <code>@</code> and allow you to
        define media queries, imports, keyframes, and more.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>At-Rules provide instructions to the CSS parser.</li>
          <li>
            Always start with <code>@</code>, followed by a keyword and block or
            statement.
          </li>
          <li>
            Common At-Rules: <code>@media</code>, <code>@import</code>,{" "}
            <code>@keyframes</code>, <code>@font-face</code>.
          </li>
          <li>
            They help in responsive design, animations, and importing external
            resources.
          </li>
          <li>
            Some At-Rules wrap CSS blocks (<code>@media</code>,{" "}
            <code>@keyframes</code>), others are standalone (
            <code>@import</code>, <code>@charset</code>).
          </li>
        </ul>
      </div>

      {/* 1️⃣ @media */}
      <h2 className="section-title">1️⃣ @media</h2>
      <p>
        Defines conditional CSS rules based on screen size or device
        characteristics.
      </p>
      <CodeCopyBlock
        language="css"
        code={`@media screen and (max-width: 600px) {
  body {
    background-color: #fef3c7;
  }
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Changes background color on small screens.
      </p>

      {/* 2️⃣ @import */}
      <h2 className="section-title">2️⃣ @import</h2>
      <p>Imports external CSS files.</p>
      <CodeCopyBlock language="css" code={`@import url("styles.css");`} />
      <p style={{ marginBottom: "30px" }}>
        👉 Loads the styles from another CSS file.
      </p>

      {/* 3️⃣ @keyframes */}
      <h2 className="section-title">3️⃣ @keyframes</h2>
      <p>Defines animations for elements.</p>
      <CodeCopyBlock
        language="css"
        code={`@keyframes slideIn {
  from { transform: translateX(-100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

div {
  animation: slideIn 1s ease-out forwards;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Animates an element sliding in from the left.
      </p>

      {/* 4️⃣ @font-face */}
      <h2 className="section-title">4️⃣ @font-face</h2>
      <p>Allows custom fonts to be used in the page.</p>
      <CodeCopyBlock
        language="css"
        code={`@font-face {
  font-family: "CustomFont";
  src: url("CustomFont.woff2") format("woff2");
}

p {
  font-family: "CustomFont", sans-serif;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Uses a custom font for paragraph text.
      </p>

      {/* Example Structure with Internal CSS */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h3 style={{ color: "#1e3a8a" }}>
          📘 Example Structure (with Internal CSS):
        </h3>
        <CodeCopyBlock
          language="html"
          code={`<style>
@media screen and (max-width: 600px) {
  body {
    background-color: #fef3c7;
  }
}

@keyframes slideIn {
  from { transform: translateX(-100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

div.animated-box {
  width: 100px;
  height: 100px;
  background-color: #10b981;
  animation: slideIn 1s ease-out forwards;
}
</style>

<div class="animated-box"></div>`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>@media</code> with Flexbox/Grid for responsive
            layouts.
          </li>
          <li>
            Create smooth animations by chaining <code>@keyframes</code> with{" "}
            <code>transition</code>.
          </li>
          <li>
            Use <code>@font-face</code> for custom typography in web projects.
          </li>
          <li>
            Explore other At-Rules like <code>@supports</code> and{" "}
            <code>@charset</code> for advanced CSS control.
          </li>
        </ul>
      </div>
    </div>
  );
}
